import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, ArrowBack } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Turnstile } from '@marsidev/react-turnstile';
import { useActionLock } from '../hooks/useActionLock';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACJtU8JtT53CUeTx';

const SignIn = () => {
  const navigate = useNavigate();
  const { loginStep1, loginStep2, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [submitLocked, runSubmit] = useActionLock();
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);
  const turnstileRef = useRef(null);

  // Two-step login state
  const [loginStep, setLoginStep] = useState(1); // 1 = credentials, 2 = OTP
  const [sessionId, setSessionId] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleOtpChange = (index, value) => {
    if (error) setError('');
    if (successMessage) setSuccessMessage('');

    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = numericValue;
      setOtp(newOtp);

      // Move to next input if value entered
      if (numericValue && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    } else if (numericValue.length === 6) {
      // Handle paste of full OTP
      const newOtp = numericValue.split('');
      setOtp(newOtp);
      otpRefs.current[5]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      otpRefs.current[lastFilledIndex]?.focus();
    }
  };

  const handleTurnstileSuccess = (token) => {
    setTurnstileToken(token);
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setError('Cloudflare verification failed. Please try again.');
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  // Handle Step 1 - Credential verification
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate Turnstile
    if (!turnstileToken) {
      setError('Please complete the Cloudflare verification.');
      return;
    }

    try {
      const result = await loginStep1(formData.email, formData.password, turnstileToken);

      if (result.success) {
        if (result.otpRequired) {
          // Move to step 2 for OTP verification
          setSessionId(result.sessionId);
          setLoginStep(2);
          setSuccessMessage(result.message || 'OTP sent to your email');
          // Focus on first OTP input
          setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } else {
          // Login complete (OTP disabled)
          navigate('/');
        }
      } else {
        setError(result.error || 'Login failed. Please try again.');
        // Reset Turnstile on error
        if (turnstileRef.current) {
          turnstileRef.current.reset();
          setTurnstileToken(null);
        }
      }
    } catch (err) {
      console.error('Login step 1 error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Handle Step 2 - OTP verification
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const otpValue = otp.join('');

    // Validate OTP
    if (otpValue.length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
      return;
    }

    try {
      const result = await loginStep2(sessionId, otpValue);

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'OTP verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Login step 2 error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Go back to step 1
  const handleBackToStep1 = () => {
    setLoginStep(1);
    setSessionId(null);
    setOtp(['', '', '', '', '', '']);
    setError('');
    setSuccessMessage('');
  };

  // Unified submit handler (click-lock prevents double submit)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (loginStep === 1) {
      runSubmit(() => handleStep1Submit(e));
    } else {
      runSubmit(() => handleStep2Submit(e));
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#F8FAFC',
      '& fieldset': {
        borderColor: '#D1D9E0',
      },
      '&:hover fieldset': {
        borderColor: '#4E83BD',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4E83BD',
        borderWidth: '2px',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '14px',
      color: '#2C3E50',
      padding: '14px',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        padding: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 450,
          width: '100%',
          padding: 4,
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          position: 'relative',
        }}
      >
        {/* Logo/Title Section */}
        <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#2C3E50',
              marginBottom: 1,
              fontSize: '28px',
            }}
          >
            Meri Zimmedari
          </Typography>
        </Box>

        {/* Welcome Text */}
        <Box sx={{ marginBottom: 3, textAlign: 'center' }}>
          {loginStep === 2 && (
            <IconButton
              onClick={handleBackToStep1}
              sx={{
                position: 'absolute',
                left: 16,
                top: 16,
                color: '#6B7280',
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#2C3E50',
              marginBottom: 0.5,
              fontSize: '24px',
            }}
          >
            {loginStep === 1 ? 'Welcome Back' : 'Verify OTP'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              fontSize: '14px',
            }}
          >
            {loginStep === 1
              ? 'Please sign in to your account'
              : `Enter the 6-digit code sent to ${formData.email}`}
          </Typography>
        </Box>

        {/* Sign In Form */}
        <form onSubmit={handleSubmit}>
          {/* Success Alert */}
          {successMessage && (
            <Alert severity="success" sx={{ marginBottom: 2.5 }}>
              {successMessage}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ marginBottom: 2.5 }}>
              {error}
            </Alert>
          )}

          {/* Step 1: Email, Password, Turnstile */}
          {loginStep === 1 && (
            <>
              {/* Email Field */}
          <Box sx={{ marginBottom: 2.5 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#2C3E50',
                marginBottom: 1,
              }}
            >
              Email Address
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#6B7280', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ marginBottom: 2.5 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#2C3E50',
                marginBottom: 1,
              }}
            >
              Password
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#6B7280', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#6B7280' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />
          </Box>

              {/* Cloudflare Turnstile */}
              <Box
                sx={{
                  marginBottom: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={handleTurnstileSuccess}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpire}
                  options={{
                    theme: 'light',
                    size: 'normal',
                  }}
                />
                {turnstileToken && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#10B981',
                      fontWeight: 500,
                    }}
                  >
                    Verification successful
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Step 2: OTP Verification */}
          {loginStep === 2 && (
            <>
              {/* OTP Field - 6 Separate Boxes */}
              <Box sx={{ marginBottom: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#2C3E50',
                    marginBottom: 1,
                  }}
                >
                  Enter OTP
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (otpRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                      inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        style: {
                          textAlign: 'center',
                          fontSize: '20px',
                          fontWeight: 600,
                          padding: '12px 0',
                        },
                      }}
                      sx={{
                        width: '52px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: '#F8FAFC',
                          '& fieldset': {
                            borderColor: digit ? '#4E83BD' : '#D1D9E0',
                            borderWidth: digit ? '2px' : '1px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#4E83BD',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4E83BD',
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#2C3E50',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Sign In / Verify Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitLocked || (loginStep === 1 && !turnstileToken)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#4E83BD',
              color: 'white',
              fontSize: '15px',
              padding: '12px',
              borderRadius: '8px',
              fontWeight: 600,
              marginBottom: 2,
              '&:hover': {
                backgroundColor: '#3A6A99',
              },
              '&:disabled': {
                backgroundColor: '#A0B4C7',
                color: 'white',
              },
            }}
          >
            {submitLocked ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <span>{loginStep === 1 ? 'Verifying...' : 'Verifying OTP...'}</span>
              </Box>
            ) : (
              loginStep === 1 ? 'Continue' : 'Verify OTP'
            )}
          </Button>

          {/* Forgot Password Link - Only show in Step 1 */}
          {loginStep === 1 && (
            <Box sx={{ textAlign: 'center' }}>
              <Link
                to="/forgot-password"
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#4E83BD',
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot Password?
                </Typography>
              </Link>
            </Box>
          )}

          {/* Resend OTP - Only show in Step 2 */}
          {loginStep === 2 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#6B7280',
                }}
              >
                Didn't receive the code?{' '}
                <Typography
                  component="span"
                  onClick={handleBackToStep1}
                  sx={{
                    fontSize: '13px',
                    color: '#4E83BD',
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Try again
                </Typography>
              </Typography>
            </Box>
          )}
        </form>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', marginTop: 4, paddingTop: 3, borderTop: '1px solid #ECF0F1' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              fontSize: '12px',
            }}
          >
            © 2025 Meri Zimmedari. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignIn;
